// Shared ASW settings types, hooks, and UI section.
// Used in AutomaticSupportWorkflowPage (redirect tab) and SettingsPage (module settings).

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router-dom'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Select,
  Switch,
  useToast,
} from '@/components/ui'

// ─── Types ────────────────────────────────────────────────────────────────────

export type AswTarget = 'SAP' | 'NONE'
export type SapDocumentMode = 'draft' | 'invoice'
export type AiMode = 'always' | 'never' | 'customer_privacy_default_yes' | 'customer_privacy_default_no'

export interface TenantWorkflowSettings {
  tenantId: string
  tenantName: string
  tenantCode: string
  target: AswTarget
  sapDocumentMode: SapDocumentMode
  devMode: boolean
  emailEnabled: boolean
  emailSenderId: string
  emailSharedMailboxId: string
  emailTemplateId: string
  sendEmailForDrafts: boolean
  aiMode: AiMode
}

export interface AswEmailTemplate {
  id: string
  name: string
  subjectTemplate: string
  bodyTemplate: string
  active: boolean
  createdAt: string
  updatedAt: string
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useTenantWorkflowSettings() {
  return useQuery<TenantWorkflowSettings[]>({
    queryKey: ['asw-tenant-settings'],
    queryFn: async () => {
      const res = await apiClient.get<TenantWorkflowSettings[]>('/automatic-support-workflow/tenant-settings')
      return res.data
    },
  })
}

export function useUpdateTenantWorkflowSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ tenantId, patch }: { tenantId: string; patch: Partial<Omit<TenantWorkflowSettings, 'tenantId' | 'tenantName' | 'tenantCode'>> }) => {
      const res = await apiClient.patch<TenantWorkflowSettings>(`/automatic-support-workflow/tenant-settings/${tenantId}`, patch)
      return res.data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['asw-tenant-settings'] }),
  })
}

export function useAswEmailTemplates() {
  return useQuery<AswEmailTemplate[]>({
    queryKey: ['asw-email-templates'],
    queryFn: async () => {
      const res = await apiClient.get<AswEmailTemplate[]>('/automatic-support-workflow/email-templates')
      return res.data
    },
  })
}

export function useAswM365Users() {
  return useQuery<Array<{ id: string; name: string; email: string }>>({
    queryKey: ['asw-m365-users'],
    queryFn: async () => {
      const res = await apiClient.get<Array<{ id: string; name: string; email: string }>>('/automatic-support-workflow/m365-users')
      return res.data
    },
    staleTime: 120_000,
  })
}

export function useAswSharedMailboxes() {
  return useQuery<Array<{ id: number; display_name: string; email_address: string; transport_type: string }>>({
    queryKey: ['asw-shared-mailboxes'],
    queryFn: async () => {
      const res = await apiClient.get<Array<{ id: number; display_name: string; email_address: string; transport_type: string }>>('/automatic-support-workflow/shared-mailboxes')
      return res.data
    },
    staleTime: 120_000,
  })
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TARGET_OPTIONS: Array<{ value: AswTarget; label: string }> = [
  { value: 'NONE', label: 'Kein Export' },
  { value: 'SAP', label: 'SAP' },
]

const SAP_MODE_OPTIONS: Array<{ value: SapDocumentMode; label: string }> = [
  { value: 'draft', label: 'Entwurf' },
  { value: 'invoice', label: 'Rechnung' },
]

// ─── AI Test Button ───────────────────────────────────────────────────────────

interface AiTestChain {
  devModeEnabled: boolean
  devKeyAvailable: boolean
  tenantOwnKeyConfigured: boolean
  cachedGlobalKey: boolean
  globalAdapterRowExists: boolean
  globalAdapterIsActive: boolean
  globalAdapterHasKey: boolean
  envKeyAvailable: boolean
}

interface AiTestResult {
  ok: boolean
  model: string
  provider: string
  keyFound: boolean
  keySource: string | null
  error: string | null
  chain: AiTestChain
}

function ChainStep({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span className={ok ? 'text-green-700' : 'text-slate-400'}>
      {ok ? '✓' : '✗'} {label}
    </span>
  )
}

function AiTestButton({ tenantId }: { tenantId: string }) {
  const [result, setResult] = useState<AiTestResult | null>(null)
  const [loading, setLoading] = useState(false)

  const run = async () => {
    setLoading(true)
    setResult(null)
    try {
      const res = await apiClient.get<AiTestResult>(`/ai/test-config?module=rechnungen&tenantId=${encodeURIComponent(tenantId)}`)
      setResult(res.data)
    } catch (e) {
      setResult({
        ok: false, model: '?', provider: '?', keyFound: false, keySource: null, error: (e as Error).message,
        chain: { devModeEnabled: false, devKeyAvailable: false, tenantOwnKeyConfigured: false, cachedGlobalKey: false, globalAdapterRowExists: false, globalAdapterIsActive: false, globalAdapterHasKey: false, envKeyAvailable: false },
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mt-2 space-y-1.5">
      <Button variant="secondary" size="sm" onClick={run} disabled={loading}>
        {loading ? 'Teste KI…' : 'KI testen'}
      </Button>
      {result && (
        <div className={`text-xs rounded px-2 py-1.5 border ${result.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {result.ok ? (
            <>✓ KI einsatzbereit — Modell: <strong>{result.model}</strong> ({result.provider}), Key-Quelle: {result.keySource}</>
          ) : (
            <div className="space-y-1.5">
              <div>✗ KI-Fehler — Modell: <strong>{result.model}</strong>, Provider: <strong>{result.provider}</strong></div>
              {result.error && <div className="font-mono break-all">{result.error}</div>}
              <div className="space-y-0.5 border-t border-red-200 pt-1 mt-1">
                <div className="font-semibold text-xs text-red-900 mb-0.5">Resolution-Chain:</div>
                {result.chain.devModeEnabled && (
                  <div><ChainStep label="Dev-Key" ok={result.chain.devKeyAvailable} /></div>
                )}
                <div><ChainStep label={`Mandant-Key (${tenantId})`} ok={result.chain.tenantOwnKeyConfigured} /></div>
                <div><ChainStep label="Globaler Adapter-Cache" ok={result.chain.cachedGlobalKey} /></div>
                <div>
                  <ChainStep
                    label={`Globaler Adapter DB${!result.chain.globalAdapterRowExists ? ' (nicht vorhanden)' : !result.chain.globalAdapterIsActive ? ' (inaktiv!)' : !result.chain.globalAdapterHasKey ? ' (kein Key gesetzt)' : ''}`}
                    ok={result.chain.globalAdapterHasKey}
                  />
                </div>
                <div><ChainStep label="ENV-Key" ok={result.chain.envKeyAvailable} /></div>
              </div>
              {!result.chain.globalAdapterHasKey && (
                <RouterLink to="/einstellungen/adapter" className="underline block">
                  → API-Key unter Einstellungen &gt; Adapter &amp; Postfächer hinterlegen
                </RouterLink>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AswTenantSettingsSection() {
  const { data, isLoading, error } = useTenantWorkflowSettings()
  const updateMut = useUpdateTenantWorkflowSettings()
  const { data: allTemplates = [] } = useAswEmailTemplates()
  const { data: sharedMailboxes = [] } = useAswSharedMailboxes()
  const { toast } = useToast()

  const templateOptions = [
    { value: '__none__', label: '— kein Template —' },
    ...allTemplates.map((t) => ({ value: t.id, label: t.name })),
  ]

  const mailboxOptions = sharedMailboxes.length > 0
    ? [
        { value: '__none__', label: '— kein Absender —' },
        ...sharedMailboxes.map((m) => ({ value: String(m.id), label: `${m.display_name} (${m.email_address})` })),
      ]
    : [{ value: '__none__', label: 'Keine Zentralen Postfächer konfiguriert' }]

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState title="Einstellungen konnten nicht geladen werden" description={(error as Error).message} />
  if (!data || data.length === 0) return <EmptyState title="Keine aktiven Mandanten" />

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Konfiguriert, wohin freigegebene Supports pro Mandant übertragen werden.
        Wenn „SAP" gewählt ist, kann zwischen Entwurf und Rechnung gewählt werden.
      </p>

      {data.map((s) => (
        <Card key={s.tenantId}>
          <div className="p-4 flex items-center justify-between gap-6">
            <div className="min-w-0">
              <div className="font-medium text-sm">{s.tenantName}</div>
              <div className="text-xs text-muted-foreground">{s.tenantCode} · ID {s.tenantId}</div>
            </div>

            <div className="flex items-center gap-4 shrink-0">
              <Select<AswTarget>
                value={s.target}
                options={TARGET_OPTIONS}
                label="Ziel"
                triggerClassName="min-w-[140px]"
                onValueChange={(val) => {
                  updateMut.mutate(
                    { tenantId: s.tenantId, patch: { target: val } },
                    { onError: (err) => toast({ title: 'Fehler beim Speichern', description: (err as Error).message, variant: 'destructive' }) },
                  )
                }}
              />

              {s.target === 'SAP' && (
                <Select<SapDocumentMode>
                  value={s.sapDocumentMode}
                  options={SAP_MODE_OPTIONS}
                  label="Modus"
                  triggerClassName="min-w-[140px]"
                  onValueChange={(val) => {
                    updateMut.mutate(
                      { tenantId: s.tenantId, patch: { sapDocumentMode: val } },
                      { onError: (err) => toast({ title: 'Fehler beim Speichern', description: (err as Error).message, variant: 'destructive' }) },
                    )
                  }}
                />
              )}

              <div className="flex items-center gap-2">
                <Switch
                  id={`devmode-${s.tenantId}`}
                  checked={s.devMode}
                  onChange={(e) => {
                    updateMut.mutate(
                      { tenantId: s.tenantId, patch: { devMode: e.target.checked } },
                      { onError: (err) => toast({ title: 'Fehler beim Speichern', description: (err as Error).message, variant: 'destructive' }) },
                    )
                  }}
                />
                <label
                  htmlFor={`devmode-${s.tenantId}`}
                  className={`text-sm font-medium cursor-pointer ${s.devMode ? 'text-amber-700' : 'text-muted-foreground'}`}
                >
                  Dev-Modus
                </label>
              </div>
            </div>
          </div>

          {s.target === 'SAP' && (
            <div className="border-t border-border px-4 py-3 space-y-3 bg-muted/30">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">E-Mail-Versand</div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">E-Mail-Versand aktiviert</div>
                  <div className="text-xs text-muted-foreground">Rechnung nach SAP-Übertragung per E-Mail senden</div>
                </div>
                <Switch
                  id={`email-${s.tenantId}`}
                  checked={s.emailEnabled}
                  onChange={(e) => {
                    updateMut.mutate(
                      { tenantId: s.tenantId, patch: { emailEnabled: e.target.checked } },
                      { onError: (err) => toast({ title: 'Fehler beim Speichern', description: (err as Error).message, variant: 'destructive' }) },
                    )
                  }}
                />
              </div>

              {s.emailEnabled && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Absender (Zentrales Postfach)</label>
                    <Select<string>
                      value={s.emailSharedMailboxId || '__none__'}
                      options={mailboxOptions}
                      triggerClassName="w-full"
                      onValueChange={(val) => {
                        updateMut.mutate(
                          { tenantId: s.tenantId, patch: { emailSharedMailboxId: val === '__none__' ? '' : val } },
                          { onError: (err) => toast({ title: 'Fehler beim Speichern', description: (err as Error).message, variant: 'destructive' }) },
                        )
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">E-Mail-Template</label>
                    <Select<string>
                      value={s.emailTemplateId || '__none__'}
                      options={templateOptions}
                      triggerClassName="w-full"
                      onValueChange={(val) => {
                        updateMut.mutate(
                          { tenantId: s.tenantId, patch: { emailTemplateId: val === '__none__' ? '' : val } },
                          { onError: (err) => toast({ title: 'Fehler beim Speichern', description: (err as Error).message, variant: 'destructive' }) },
                        )
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">E-Mail für Entwürfe</div>
                      <div className="text-xs text-muted-foreground">Auch bei SAP-Entwurf (Draft) eine E-Mail senden</div>
                    </div>
                    <Switch
                      checked={s.sendEmailForDrafts}
                      onChange={(e) => {
                        updateMut.mutate(
                          { tenantId: s.tenantId, patch: { sendEmailForDrafts: e.target.checked } },
                          { onError: (err) => toast({ title: 'Fehler beim Speichern', description: (err as Error).message, variant: 'destructive' }) },
                        )
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <div className="border-t border-border px-4 py-3 space-y-3 bg-muted/30">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">KI-Rechnungstexte</div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">KI-Modus für Ticket-Positionen</label>
              <Select<string>
                value={s.aiMode || 'never'}
                options={[
                  { value: 'never', label: 'Nie — KI deaktiviert (Standard)' },
                  { value: 'always', label: 'Immer — KI für alle Ticket-Positionen' },
                  { value: 'customer_privacy_default_yes', label: 'Kundeneinstellung (Standard: Ja)' },
                  { value: 'customer_privacy_default_no', label: 'Kundeneinstellung (Standard: Nein)' },
                ]}
                triggerClassName="w-full"
                onValueChange={(val) => {
                  updateMut.mutate(
                    { tenantId: s.tenantId, patch: { aiMode: val as AiMode } },
                    { onError: (err) => toast({ title: 'Fehler beim Speichern', description: (err as Error).message, variant: 'destructive' }) },
                  )
                }}
              />
              <div className="text-xs text-muted-foreground mt-1">
                KI generiert Überschriften und Texte für Problemstellung, Durchgeführtes und Nächste Schritte.
                Nur für Positionen vom Typ „Ticket". Standard: Nie.
              </div>
            </div>
            {s.aiMode !== 'never' && <AiTestButton tenantId={s.tenantId} />}
          </div>

          {s.devMode && (
            <div className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700 rounded-b-md">
              Dev-Modus aktiv — SAP-Calls werden simuliert; E-Mails gehen an tryba@implec.de
            </div>
          )}
        </Card>
      ))}
    </div>
  )
}
